import useSWR from 'swr';
import { Loader2, Repeat, XCircle, RotateCcw, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchAdminSubscriptions,
  cancelAdminSubscription,
  resumeAdminSubscription,
} from '@/lib/api';

const AUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLE = {
  active:     'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  trialing:   'bg-sky-500/15 text-sky-300 border-sky-500/30',
  past_due:   'bg-amber-500/15 text-amber-300 border-amber-500/30',
  canceled:   'bg-white/5 text-white/40 border-white/10',
  incomplete: 'bg-white/5 text-white/40 border-white/10',
  unpaid:     'bg-red-500/15 text-red-300 border-red-500/30',
};

const fmtNextRenewal = (unix) => {
  if (!unix) return '—';
  try {
    const d = new Date(Number(unix) * 1000);
    const days = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    const nice = d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
    if (days < 0) return `${nice} (overdue)`;
    if (days === 0) return `${nice} (today)`;
    if (days === 1) return `${nice} (tomorrow)`;
    return `${nice} (in ${days}d)`;
  } catch { return String(unix); }
};

export default function SubscriptionsPanel() {
  const { data: subs = [], isLoading, mutate } = useSWR('admin-subscriptions', fetchAdminSubscriptions);

  const onCancel = async (sub) => {
    if (!window.confirm(`Schedule cancellation at end of period for ${sub.customer_email || sub.subscription_id}?`)) return;
    try {
      await cancelAdminSubscription(sub.subscription_id);
      toast.success('Subscription set to cancel at period end');
      mutate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const onResume = async (sub) => {
    try {
      await resumeAdminSubscription(sub.subscription_id);
      toast.success('Cancellation reversed — subscription continues');
      mutate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed');
    }
  };

  const activeCount = subs.filter((s) => ['active', 'trialing', 'past_due'].includes(s.status)).length;

  return (
    <div data-testid="admin-subscriptions">
      <div className="flex items-end justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-white/55">
            {activeCount} active · {subs.length} total
          </div>
          <h2 className="font-display font-light text-3xl sm:text-4xl mt-2">Subscriptions</h2>
          <p className="text-white/55 text-sm mt-2 max-w-md">
            Every Stripe subscription created through the shop. Status pulled live from Stripe.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
      ) : subs.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center text-white/50">
          <Repeat size={20} className="mx-auto mb-3 text-white/30" />
          No subscriptions yet.
        </div>
      ) : (
        <div className="border border-white/10">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 text-[10px] uppercase tracking-[0.28em] text-white/45 bg-white/[0.02]">
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Cadence</div>
            <div className="col-span-2">Next renewal</div>
            <div className="col-span-1 text-right">Total</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {subs.map((s) => {
            const scheduled = s.cancel_at_period_end;
            const badgeCls = STATUS_STYLE[s.status] || STATUS_STYLE.incomplete;
            return (
              <div
                key={s.subscription_id}
                data-testid={`admin-sub-${s.subscription_id}`}
                className="grid grid-cols-12 gap-4 items-center px-5 py-4 border-t border-white/5 hover:bg-white/[0.02] transition-colors first:border-t-0"
              >
                <div className="col-span-12 md:col-span-3 min-w-0">
                  <div className="text-white truncate">{s.customer_name || s.customer_email || 'Guest'}</div>
                  <div className="text-[11px] text-white/45 truncate">{s.customer_email}</div>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] border px-2 py-1 ${badgeCls}`}>
                    {s.status}
                  </span>
                  {scheduled && (
                    <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-amber-400">Cancels at period end</div>
                  )}
                </div>
                <div className="col-span-6 md:col-span-2 text-[11px] uppercase tracking-[0.22em] text-white/60">
                  {s.frequency_label || s.frequency || '—'}
                </div>
                <div className="col-span-6 md:col-span-2 text-[11px] uppercase tracking-[0.22em] text-white/60 inline-flex items-center gap-1.5">
                  <Calendar size={11} /> {fmtNextRenewal(s.current_period_end)}
                </div>
                <div className="col-span-6 md:col-span-1 text-right font-display text-white tabular-nums">
                  {AUD(s.total)}
                </div>
                <div className="col-span-12 md:col-span-2 text-right">
                  {scheduled ? (
                    <button
                      type="button"
                      onClick={() => onResume(s)}
                      data-testid={`admin-sub-resume-${s.subscription_id}`}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-emerald-500 text-white hover:text-emerald-300 px-3 py-2 transition-colors"
                    >
                      <RotateCcw size={11} /> Resume
                    </button>
                  ) : (
                    ['active', 'trialing', 'past_due'].includes(s.status) && (
                      <button
                        type="button"
                        onClick={() => onCancel(s)}
                        data-testid={`admin-sub-cancel-${s.subscription_id}`}
                        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] border border-white/15 hover:border-red-500 text-white hover:text-red-300 px-3 py-2 transition-colors"
                      >
                        <XCircle size={11} /> Cancel
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
